import { sessionData } from "@/data/sessionData";
import { sidebarData, sidebarList } from "@/data/sidebarItem";
import { userData } from "@/data/userData";
import { loginRequest } from "@/requests/loginRequest";
import { storeToRefs } from "pinia";
import { toRefs } from "vue";
import { createRouter, createWebHistory, NavigationGuardNext, RouteLocationNormalized } from "vue-router";

const routes = [
  {
    path: "/",
    redirect: "/landingPage",
  },
  {
    path: "/dashboard",
    component: () => import("@/components/FullLayout.vue"),
    children: [
      {
        name: "Dashboard",
        path: "/dashboard",
        component: () => import("@/components/sidebar/Devices.vue")
      },
      {
        name: "2FA",
        path: "2fa",
        component: () => import("@/components/sidebar/2FaAuth.vue")
      },
      {
        name: "Activity",
        path: "activity",
        component: () => import("@/components/sidebar/Activity.vue")
      },
      {
        name: "Incidents",
        path: "incidents",
        component: () => import("@/components/sidebar/Incidents.vue")
      },
      {
        name: "Installer",
        path: "installer",
        component: () => import("@/components/sidebar/Installer.vue")
      },
      {
        name: "Agents",
        path: "agents",
        component: () => import("@/components/sidebar/Agents.vue")
      },
      {
        name: "FAQ",
        path: "faq",
        component: () => import("@/components/sidebar/Faq.vue")
      },
      {
        name: "Supports",
        path: "supports",
        component: () => import("@/components/sidebar/Supports.vue")
      },
      {
        name: "ProfileUser",
        path: "profileUser",
        component: () => import("@/components/policy/PolicySetting.vue")
      },
    ],
  },
  { path: "/login", name: "Login", component: () => import("@/components/signin/LogInComponent.vue") },
  { path: '/signup', name: 'Signup', component: () => import("@/components/signup/SignUpComponent.vue") },
  { path: '/resetpwd', name: 'Reset Password', component: () => import("@/components/popup/ResetPassword.vue") },

  { path: '/wcSignIn/:lang?', name: 'wcSignIn', component: () => import("@/components/signin/WCLogInFields.vue") }, // wooCommerce router (iframe). Needed to use other theme (nb theme group)
  { path: '/wcSignUp/:lang?', name: 'wcSignUp', component: () => import("@/components/signup/WCSignUpFields.vue") }, // wooCommerce router (iframe). Needed to use other theme (nb theme group)
  { path: '/wcResetPwd/:lang?', name: 'wcResetPwd', component: () => import("@/components/popup/WCRequestNewPasswordFields.vue") }, // wooCommerce router (iframe). Needed to use other theme (nb theme group)

  { path: '/fullLayout', name: 'FullLayout', component: () => import("@/components/FullLayout.vue") },
  { path: '/landingPage', name: 'Landing page', component: () => import("@/components/LandingPage.vue") },
  { path: `/landingPage/NobugsAdminSentineloneZimperiumMGMT`, name: 'Admin', component: () => import("@/admin/Dashboard.vue") },

  { path: '/expLic', name: 'Expiration License', component: () => import("@/components/ExpirationLicense.vue") },
  { path: '/confirm', name: 'Confirm', component: () => import("@/components/ConfirmEmail.vue") }, // confirm email supabase
  { path: '/post-confirm', name: 'PostConfirm', component: () => import("@/components/PostConfirm.vue") }, // post-confirmation SSO + shop redirect

  { path: '/signinFields', name: 'SigninFields', component: () => import("@/components/signin/LogInFields.vue") }, // my fields, used for this app
  { path: '/signupFields', name: 'SignupFields', component: () => import("@/components/signup/SignUpFields.vue") }, // my fields, used for this app



  { path: '/resetpwdFields', name: 'Reset Pwd Fields', component: () => import("@/components/popup/RequestNewPasswordFields.vue") },

]

const router = createRouter({
  history: createWebHistory(),
  routes: routes
});

router.beforeEach(async (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {

  if (to.path === "/landingPage/NobugsAdminSentineloneZimperiumMGMT") {
    return next()
  }

  if (to.name === 'Confirm' || to.name === 'PostConfirm' ||
    to.name === "Login" || to.name === "Signup" || to.name === "Reset Password" ||
    to.name === "wcSignIn" || to.name === "wcSignUp" || to.name === "wcResetPwd") {
    return next()
  }

  const sessionStore = sessionData()
  const { validSession } = toRefs(sessionStore)
  const { getUserData } = loginRequest()
  const sidebarStore = sidebarData()
  const { selectedSideBar } = toRefs(sidebarStore)
  const userStore = userData()
  const { user } = storeToRefs(userStore)

  let data
  if (to.path !== "/landingPage") {
    data = await getUserData()
  }


  if (to.name !== 'Landing page' &&
    to.name !== 'Reset Password' &&
    to.name !== "Expiration License") {

    // Assign fresh data BEFORE checking licence expiry so we never read stale persisted state
    if (data) user.value = data

    if (!data || !data.id) {
      validSession.value = false

      if (to.name !== 'Landing page') {
        return next({ name: "Landing page" })
      } else {
        return next()
      }
    }

    // Check after assigning fresh data so stale persisted state is never used
    if (user.value.all_licence_expired && _from.path !== "/expLic" && to.path !== "/expLic") {
      return next({ name: "Expiration License" });
    }
  }

  // assign the selected sidebar tab, visualization of design
  if (to.name !== null && to.name !== undefined) {
    const found = sidebarList.find(si => si.title.toLowerCase().includes((to.name as string).toLowerCase()))
    selectedSideBar.value = found ? found.title : ""
  }

  return next()

});


export default router;
